/**
 * Media and Interaction Handler Utilities
 *
 * This file contains utility functions for media handling and user interactions
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

import { validateImageFile, getUnsupportedFormatMessage } from "./imageFormatUtils";
import imageCompression from "browser-image-compression";
import { preprocessRasterImageForCanvas } from "./imageUploadUtils";

/**
 * Handle mouse wheel events for zoom and scroll
 * @param {Function} handleZoom - (factor, zoomLevel, pointerOpts?) => void
 * @param {Object} scrollAreaRef - Reference to scroll area
 * @param {() => number} getZoomLevel - Current app zoom (use ref so document wheel listener always reads latest)
 */
export const createHandleWheel =
  (handleZoom, scrollAreaRef, getZoomLevel) => (e) => {
    // Ignore wheel events from webcam popup
    const target = e.target;
    if (
      target &&
      (target.closest(".webcam-overlay") ||
        target.closest(".webcam-content") ||
        target.closest(".live-camera"))
    ) {
      return;
    }

    const zoomLevel = typeof getZoomLevel === "function" ? getZoomLevel() : 1;

    // Only zoom when Ctrl/Cmd is held down (like Figma)
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      handleZoom(delta, zoomLevel, {
        fromWheel: true,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    } else {
      // Improve regular scrolling when zoomed
      const scrollArea = scrollAreaRef.current;
      if (scrollArea && zoomLevel > 1) {
        e.preventDefault();

        // Calculate zoom-aware scroll speed with more moderate multiplier for touchpad
        const zoomMultiplier = Math.min(zoomLevel * 0.8, 3); // More moderate multiplier and cap at 3x
        const scrollSpeed = Math.max(20, 30 * zoomMultiplier); // Reduced base speed from 40 to 20/30

        // Handle both vertical and horizontal scrolling
        const deltaX = e.deltaX;
        const deltaY = e.deltaY;

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          // Vertical scrolling
          const newScrollTop =
            scrollArea.scrollTop + (deltaY > 0 ? scrollSpeed : -scrollSpeed);
          scrollArea.scrollTo({
            top: Math.max(
              0,
              Math.min(
                scrollArea.scrollHeight - scrollArea.clientHeight,
                newScrollTop
              )
            ),
            behavior: "auto",
          });
        } else if (Math.abs(deltaX) > 0) {
          // Horizontal scrolling
          const newScrollLeft =
            scrollArea.scrollLeft + (deltaX > 0 ? scrollSpeed : -scrollSpeed);
          scrollArea.scrollTo({
            left: Math.max(
              0,
              Math.min(
                scrollArea.scrollWidth - scrollArea.clientWidth,
                newScrollLeft
              )
            ),
            behavior: "auto",
          });
        }
      }
    }
  };

/**
 * Add video to canvas
 * @param {string} videoSrc - Video source URL
 * @param {Function} setVideos - Setter for videos array
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} setSelectedObjects - Setter for selected objects array
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setSelectedTextId - Setter for selected text ID
 * @param {Function} setSelectedArrow - Setter for selected arrow
 * @param {Function} setSelectedLine - Setter for selected line
 * @param {Function} setSelectedDoubleSidedArrow - Setter for selected double-sided arrow
 * @param {Function} setSelectedVideo - Setter for selected video
 * @param {Function} saveToHistory - Function to save to history
 * @param {Function} generateMarkerId - Function to generate unique IDs
 * @param {Function} generateVideoThumbnail - Function to generate video thumbnails
 * @param {Object} transformerRef - Reference to transformer
 */
export const createAddVideo =
  (
    setVideos,
    setSelectedObject,
    setSelectedObjects,
    setSelectedImage,
    setSelectedText,
    setSelectedTextId,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    setSelectedVideo,
    saveToHistory,
    generateMarkerId,
    generateVideoThumbnail,
    transformerRef
  ) =>
  (videoSrc) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = videoSrc;

    video.onloadedmetadata = async () => {
      const duration = video.duration;

      // Generate thumbnail immediately
      const thumbnail = await generateVideoThumbnail(videoSrc, 320, 180);

      setVideos((prevVideos) => {
        const videoNumber = prevVideos.length + 1;
        const newVideo = {
          id: generateMarkerId(),
          src: videoSrc,
          x: 100,
          y: 100,
          width: 320,
          height: 180,
          duration, // ✅ Now correct duration
          videoNumber,
          name: `Video ${videoNumber}`,
          thumbnail, // ✅ Add thumbnail immediately
        };

        // Automatically select the newly created video after a short delay
        // to ensure the DOM node is rendered
        setTimeout(() => {
          // Clear all previous selections first
          setSelectedObject(null);
          setSelectedObjects([]);
          setSelectedImage(null);
          setSelectedText(null);
          setSelectedTextId(null);
          setSelectedArrow(null);
          setSelectedLine(null);
          setSelectedDoubleSidedArrow(null);

          // Clear transformer
          if (transformerRef.current) {
            transformerRef.current.nodes([]);
          }

          // Set the new video as selected
          setSelectedObject({
            ...newVideo,
            type: "Video",
          });
          setSelectedVideo(newVideo);
        }, 50);

        return [...prevVideos, newVideo];
      });

      // Use setTimeout to ensure state update has completed
      setTimeout(() => {
        saveToHistory("add_video");
      }, 0);
    };

    video.onerror = async () => {
      // Still try to generate thumbnail even if metadata fails
      const thumbnail = await generateVideoThumbnail(videoSrc, 320, 180);

      setVideos((prevVideos) => {
        const videoNumber = prevVideos.length + 1;
        const newVideo = {
          id: generateMarkerId(),
          src: videoSrc,
          x: 100,
          y: 100,
          width: 320,
          height: 180,
          duration: 0,
          videoNumber,
          name: `Video ${videoNumber}`,
          thumbnail, // ✅ Add thumbnail even on error
        };

        // Automatically select the newly created video after a short delay
        // to ensure the DOM node is rendered
        setTimeout(() => {
          // Clear all previous selections first
          setSelectedObject(null);
          setSelectedObjects([]);
          setSelectedImage(null);
          setSelectedText(null);
          setSelectedTextId(null);
          setSelectedArrow(null);
          setSelectedLine(null);
          setSelectedDoubleSidedArrow(null);

          // Clear transformer
          if (transformerRef.current) {
            transformerRef.current.nodes([]);
          }

          // Set the new video as selected
          setSelectedObject({
            ...newVideo,
            type: "Video",
          });
          setSelectedVideo(newVideo);
        }, 50);

        return [...prevVideos, newVideo];
      });

      // Use setTimeout to ensure state update has completed
      setTimeout(() => {
        saveToHistory("add_video");
      }, 0);
    };
  };

/**
 * Convert SVG to JPEG/PNG canvas
 * @param {string} svgDataUrl - SVG data URL
 * @returns {Promise<string>} - JPEG data URL
 */
export const convertSvgToJpeg = (svgDataUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        // Get SVG dimensions - try naturalWidth/Height first, then width/height, then parse from SVG
        let svgWidth = img.naturalWidth || img.width;
        let svgHeight = img.naturalHeight || img.height;

        // If dimensions are still not available, try to parse from the SVG data URL
        if (
          (!svgWidth || !svgHeight || svgWidth === 0 || svgHeight === 0) &&
          svgDataUrl
        ) {
          try {
            // Extract SVG content from data URL (base64 or URL encoded)
            const base64Data = svgDataUrl.split(",")[1];
            let svgContent;

            // Try base64 decode first, then URL decode
            try {
              svgContent = atob(base64Data);
            } catch {
              svgContent = decodeURIComponent(base64Data);
            }

            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
            const svgElement = svgDoc.documentElement;

            // Try to get viewBox or width/height attributes
            const viewBox = svgElement.getAttribute("viewBox");
            if (viewBox) {
              const parts = viewBox.split(/\s+/);
              if (parts.length >= 4) {
                svgWidth = parseFloat(parts[2]) || 800;
                svgHeight = parseFloat(parts[3]) || 600;
              }
            } else {
              svgWidth = parseFloat(svgElement.getAttribute("width")) || 800;
              svgHeight = parseFloat(svgElement.getAttribute("height")) || 600;
            }
          } catch (parseError) {
            console.warn(
              "Could not parse SVG dimensions, using defaults:",
              parseError
            );
          }
        }

        // Use defaults if still no dimensions
        svgWidth = svgWidth && svgWidth > 0 ? svgWidth : 800;
        svgHeight = svgHeight && svgHeight > 0 ? svgHeight : 600;

        // Create canvas with the same dimensions as the SVG
        const canvas = document.createElement("canvas");
        canvas.width = svgWidth;
        canvas.height = svgHeight;

        const ctx = canvas.getContext("2d");

        // Fill white background for transparency (SVGs often have transparent backgrounds)
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the SVG image onto the canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert canvas to JPEG data URL with high quality
        const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.95);
        resolve(jpegDataUrl);
      } catch (error) {
        reject(new Error(`Failed to convert SVG to JPEG: ${error.message}`));
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load SVG image"));
    };

    img.src = svgDataUrl;
  });
};

/**
 * Handle image upload from file input
 * @param {Event} event - File input change event
 * @param {Function} setAction - Setter for action state
 * @param {Function} setIsImageLoading - Setter for image loading state
 * @param {Function} setImageLoadingProgress - Setter for loading progress
 * @param {Function} addImage - Function to add image to canvas
 * @param {Object} ACTIONS - Actions constants object
 * @param {Function} setPendingImageData - Setter for pending image data
 * @param {Function} showErrorSnackbar - Function to show error snackbar
 */
// Threshold (bytes): for files larger than this, use blob URL instead of readAsDataURL
// to avoid loading the entire file as base64 in memory (keeps UI snappy).
const LARGE_FILE_THRESHOLD_BYTES = 2 * 1024 * 1024; // 2MB

export const createHandleImageUpload =
  (setAction, setIsImageLoading, setImageLoadingProgress, addImage, ACTIONS, setPendingImageData, showErrorSnackbar = null) =>
  async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate image format before processing
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        // Use showErrorSnackbar if available, otherwise fallback to alert
        if (showErrorSnackbar && typeof showErrorSnackbar === "function") {
          showErrorSnackbar(validation.error);
        } else {
          alert(validation.error);
        }
        event.target.value = null;
        return;
      }

      // Check if it's an SVG file
      const isSvg =
        file.type === "image/svg+xml" ||
        file.name.toLowerCase().endsWith(".svg");

      // Keep original file for S3/export, but allow a smaller UI copy for canvas display.
      const originalFile = file;

      let rasterPrepLoaderActive = false;
      const finishUploadPrepLoader = () => {
        if (rasterPrepLoaderActive) {
          setIsImageLoading(false);
          setImageLoadingProgress(0);
          rasterPrepLoaderActive = false;
        }
      };

      // Single overlay during raster prep (compression + checkerboard + decode).
      if (!isSvg) {
        rasterPrepLoaderActive = true;
        setIsImageLoading(true);
        setImageLoadingProgress(15);
      }

      // For very large raster images, aggressively downscale/compress for UI so the canvas never
      // has to draw 30MB+ bitmaps every frame. Original is still available via originalBlob.
      let uiFile = file;
      if (!isSvg && file.size > 10 * 1024 * 1024) {
        try {
          const compressed = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1500,
            useWebWorker: true,
          });
          uiFile = compressed;
        } catch (e) {
          console.warn("Image auto-compression failed, using original file for UI:", e);
        }
      }

      // Same checkerboard→transparency pass as paste/drop (preview sites often bake the grid in).
      let displayRaster = uiFile;
      if (!isSvg) {
        try {
          displayRaster = await preprocessRasterImageForCanvas(uiFile);
          setImageLoadingProgress(55);
        } catch (e) {
          console.warn("Canvas: raster preprocess (checkerboard) skipped:", e);
        }
      }

      const isLargeFile = displayRaster.size > LARGE_FILE_THRESHOLD_BYTES;

      const loadFromBlobUrl = (blobUrl, fileToPass) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setPendingImageData({
            imageSrc: blobUrl,
            originalBlob: fileToPass,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            loadedImage: img,
          });
          setAction(ACTIONS.IMAGE);
          finishUploadPrepLoader();
        };
        img.onerror = () => {
          finishUploadPrepLoader();
          const formatError = getUnsupportedFormatMessage(file.type, file.name);
          if (showErrorSnackbar && typeof showErrorSnackbar === "function") {
            showErrorSnackbar(formatError);
          } else {
            alert(formatError);
          }
        };
        img.src = blobUrl;
      };

      // For large files (e.g. 30MB+): use blob URL so we never load full base64 into memory.
      // This keeps the main thread responsive and avoids huge atob() + duplicate decoding.
      if (isLargeFile && !isSvg) {
        try {
          setImageLoadingProgress(70);
          const blobUrl = URL.createObjectURL(displayRaster);
          // originalFile is preserved for S3/upload; displayRaster may be checkerboard-stripped for canvas.
          loadFromBlobUrl(blobUrl, originalFile);
        } catch (error) {
          finishUploadPrepLoader();
          console.error("Error creating blob URL for large image:", error);
          if (showErrorSnackbar && typeof showErrorSnackbar === "function") {
            showErrorSnackbar("Failed to load image. Try a smaller file.");
          } else {
            alert("Failed to load image. Try a smaller file.");
          }
        }
        event.target.value = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          let result = reader.result;

          // Convert SVG to JPEG for consistent cropping behavior
          if (isSvg) {
            try {
              result = await convertSvgToJpeg(result);
            } catch (error) {
              console.error("Error converting SVG to JPEG:", error);
              alert(`Failed to convert SVG to JPEG: ${error.message}`);
              return;
            }
          }

          if (!isSvg) {
            setImageLoadingProgress(70);
          }

          // Use original image without compression, pass blob for efficient S3 upload
          // For SVG conversions, create a new blob from the JPEG data URL
          let fileToPass = originalFile;
          if (isSvg) {
            // Convert data URL to blob for the addImage function
            const response = await fetch(result);
            const blob = await response.blob();
            fileToPass = new File(
              [blob],
              file.name.replace(/\.svg$/i, ".jpg"),
              {
                type: "image/jpeg",
              }
            );
          }

          // Load image to get natural dimensions
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            // Store image data for placement instead of directly adding
            // Include the loaded image object so preview can render immediately
            setPendingImageData({
              imageSrc: result,
              originalBlob: fileToPass,
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight,
              loadedImage: img, // Store the loaded image for immediate preview
            });
            
            // Set action to IMAGE mode to show crosshair cursor
            setAction(ACTIONS.IMAGE);
            finishUploadPrepLoader();
          };
          img.onerror = () => {
            finishUploadPrepLoader();
            const formatError = getUnsupportedFormatMessage(file.type, file.name);
            // Use showErrorSnackbar if available, otherwise fallback to alert
            if (showErrorSnackbar && typeof showErrorSnackbar === "function") {
              showErrorSnackbar(formatError);
            } else {
              alert(formatError);
            }
          };
          img.src = result;
        } catch (error) {
          finishUploadPrepLoader();
          console.error("Error processing image:", error);
          const formatError = getUnsupportedFormatMessage(file.type, file.name);
          const errorMessage = `Failed to process image: ${error.message}\n\n${formatError}`;
          // Use showErrorSnackbar if available, otherwise fallback to alert
          if (showErrorSnackbar && typeof showErrorSnackbar === "function") {
            showErrorSnackbar(errorMessage);
          } else {
            alert(errorMessage);
          }
        }
      };

      reader.onerror = () => {
        finishUploadPrepLoader();
        const errorMessage = `Failed to read image file. Please ensure the file is not corrupted.`;
        // Use showErrorSnackbar if available, otherwise fallback to alert
        if (showErrorSnackbar && typeof showErrorSnackbar === "function") {
          showErrorSnackbar(errorMessage);
        } else {
          alert(errorMessage);
        }
      };

      // Use display raster (compressed + optional checkerboard strip) for data URL.
      reader.readAsDataURL(displayRaster);
    }

    // ✅ Allow same file to trigger event again
    event.target.value = null;
  };
